import React, { useEffect, useState } from 'react';
import { LuX, LuChevronRight, LuFolderPlus, LuGitBranch } from 'react-icons/lu';
import { doc, setDoc, collection, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useOrganization } from "../../../context/OrgContext";

const NewModal = ({ isOpen, onClose, onItemAdded }) => {
  const { institution } = useOrganization();
  const [selectedType, setSelectedType] = useState(null);
  const [itemName, setItemName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Convert string to slug (lowercase, hyphenated)
  const createSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedType(null);
      setItemName('');
      setIsCreating(false);
    }
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
  };

  const handleBack = () => {
    setSelectedType(null);
    setItemName('');
  };

  const handleCreate = async () => {
    if (!itemName.trim() || !selectedType || !institution?.id) return;

    setIsCreating(true);

    try {
      const slug = createSlug(itemName);

      if (selectedType === 'section') {
        // Add section to the sections array in the main institution document
        const institutionRef = doc(db, `colleges/${institution.id}`);

        // First get the current document to check if sections array exists
        const institutionDoc = await getDoc(institutionRef);
        const currentData = institutionDoc.data() || {};
        const currentSections = currentData.sections || [];

        // Add the new section to the array
        const newSection = {
          name: itemName.trim(),
          slug: slug,
          createdAt: new Date(),
          type: 'section'
        };

        const updatedSections = [...currentSections, newSection];

        // Update the document with the new sections array
        await updateDoc(institutionRef, {
          sections: updatedSections
        });

      } else if (selectedType === 'workflow') {
        // Create a document in the workflows collection
        const workflowRef = doc(db, `colleges/${institution.id}/workflows`, slug);
        await setDoc(workflowRef, {
          name: itemName.trim(),
          slug: slug,
          createdAt: new Date(),
          updatedAt: new Date(),
          type: 'workflow',
          status: 'draft',
          steps: []
        });
      }

      // Call the callback to refresh the parent component
      if (onItemAdded) {
        onItemAdded();
      }

      // Close the modal
      onClose();
    } catch (err) {
      console.error(`Error creating ${selectedType}:`, err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && itemName.trim() && !isCreating) {
      handleCreate();
    }
  };

  if (!isOpen) return null;

  const typeOptions = [
    {
      id: 'section',
      name: 'Section',
      description: 'Organize content into sections',
      icon: LuFolderPlus,
      color: 'text-blue-400'
    },
    {
      id: 'workflow',
      name: 'Workflow',
      description: 'Create a process workflow',
      icon: LuGitBranch,
      color: 'text-green-400'
    }
  ];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-[#1a1a1a] border border-[#ffffff10] rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-medium text-white">
              {selectedType ? `Create ${selectedType === 'section' ? 'Section' : 'Workflow'}` : 'What do you want to create?'}
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-[#ffffff10] rounded-lg transition-colors"
            >
              <LuX className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {!selectedType ? (
            // Type selection
            <div className="space-y-1">
              {typeOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <button
                    key={option.id}
                    onClick={() => handleTypeSelect(option.id)}
                    className="w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200 group hover:bg-[#ffffff10] cursor-pointer"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-2 rounded-lg bg-[#ffffff10] group-hover:bg-[#ffffff20] transition-colors">
                        <IconComponent className={`w-5 h-5 ${option.color}`} />
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-white text-base">
                          {option.name}
                        </div>
                        <div className="text-sm text-[#ffffff70] mt-0.5">
                          {option.description}
                        </div>
                      </div>
                    </div>
                    <LuChevronRight className="w-4 h-4 text-[#ffffff70] group-hover:text-white transition-colors" />
                  </button>
                );
              })}
            </div>
          ) : (
            // Name input
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#ffffff90] mb-2">
                  {selectedType === 'section' ? 'Section' : 'Workflow'} Name
                </label>
                <input
                  type="text"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={`Enter ${selectedType} name...`}
                  className="w-full px-4 py-3 bg-[#ffffff10] border border-[#ffffff20] rounded-lg text-white placeholder-[#ffffff50] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                  disabled={isCreating}
                />
                {itemName && (
                  <div className="text-xs text-[#ffffff50] mt-1">
                    Slug: {createSlug(itemName)}
                  </div>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleBack}
                  disabled={isCreating}
                  className="flex-1 px-4 py-3 bg-[#ffffff10] hover:bg-[#ffffff20] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!itemName.trim() || isCreating}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewModal;